import classNames from "classnames";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useState } from "react";

import { postToApp, WEBVIEW_MESSAGE_TYPES } from "@/shared/webview-bridge.constants";

import { getGiftCardProviderDisplayName } from "@/utils/gift-card-display";

import { UI_CONTEXTS } from "@/analytics/constants";
import EVENTS from "@/analytics/events";
import { trackUserEvent } from "@/analytics/tracking";

import { useCurrentLocale } from "@/context/LanguageContext";

import ActionButton from "@/components/buttons/action-button";
import { KINDS, SIZES } from "@/components/buttons/buttons.constants";

import { useInAppDarkMode } from "@/hooks/useInAppDarkMode";

import styles from "./gift-card-order-confirmed.module.scss";

type Props = {
    imageUrl: string | null;
    email: string;
    sku: string;
    supplier: string;
    isGift: boolean;
};

/*
 * In-app "Thanks for your order" confirmation for the gift card checkout webview.
 * Only rendered when the app injects apAppNativeExperienceEnabled = true, so the
 * "View gift card" deeplink is never shown to an app build that would ignore it.
 * Rendered identically in every locale, same convention as GiftCardPlacingOrder:
 * buttons force the arcade style and the header/message styles are nested under
 * the container so they win over the global .arcade heading styles that the
 * en-US layout applies.
 * Gift purchases get recipient-facing copy and keep "Done" because the card
 * lands in the recipient's wallet, not the purchaser's.
 */
export const GiftCardOrderConfirmed: React.FC<Props> = ({
    imageUrl,
    email,
    sku,
    supplier,
    isGift,
}) => {
    const { t } = useTranslation();
    const locale = useCurrentLocale();
    const router = useRouter();
    const isDark = useInAppDarkMode();
    const [imageFailed, setImageFailed] = useState(false);

    const provider = getGiftCardProviderDisplayName(supplier);
    const showImage = Boolean(imageUrl) && !imageFailed;
    const messageKey = isGift
        ? provider
            ? "giftCards.orderConfirmed.giftMessage"
            : "giftCards.orderConfirmed.giftMessageWithoutProvider"
        : provider
        ? "giftCards.orderConfirmed.message"
        : "giftCards.orderConfirmed.messageWithoutProvider";

    const onViewGiftCard = () => {
        trackUserEvent(
            locale,
            router.asPath,
            UI_CONTEXTS.GIFTCARD_ORDER_CONFIRMATION,
            EVENTS.GIFT_CARD_VIEW_GIFT_CARD_CLICK,
            { sku }
        );
        postToApp(
            WEBVIEW_MESSAGE_TYPES.OPEN_DEEP_LINK,
            `https://www.afterpay.com/${locale}/wallet/gift-cards`
        );
    };

    const onDone = () => {
        trackUserEvent(
            locale,
            router.asPath,
            UI_CONTEXTS.GIFTCARD_ORDER_CONFIRMATION,
            EVENTS.GIFT_CARD_ORDER_CONFIRMED_DONE_CLICK,
            { sku }
        );
        postToApp(WEBVIEW_MESSAGE_TYPES.ON_GIFT_CARD_CLOSE);
    };

    return (
        <div className={classNames(styles.container, { [styles.dark]: isDark })}>
            <div className={styles.cardArtFrame}>
                <div className={styles.cardTile}>
                    {showImage && (
                        <img
                            className={styles.cardImage}
                            src={imageUrl}
                            alt=""
                            onError={() => setImageFailed(true)}
                        />
                    )}
                </div>
            </div>
            <h2 className={styles.header}>{t("giftCards.orderConfirmed.header")}</h2>
            <p className={styles.message}>
                {t(messageKey, { email, ...(provider ? { provider } : {}) })}
            </p>
            <div className={styles.ctaContainer}>
                <div className={styles.doneButton}>
                    <ActionButton
                        disabled={false}
                        fluid={true}
                        forceArcade={true}
                        kind={KINDS.BLACK}
                        label={t(
                            isGift
                                ? "giftCards.orderConfirmed.done"
                                : "giftCards.orderConfirmed.viewGiftCard"
                        )}
                        onClick={isGift ? onDone : onViewGiftCard}
                        size={SIZES.LARGE}
                    />
                </div>
            </div>
        </div>
    );
};
